'use client';

import { apiClient } from '@/lib/apiClient';
import { UserRole } from '@/types/auth';
import { Clinic } from '@/types/clinic';
import { RequiredDocument } from '@/types/documents';
import { ClinicUserRole, User } from '@/types/user';
import { useRouter } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

interface AuthContextType {
  user: User | null;
  role: UserRole | null;
  isSuperAdmin: boolean;
  userRoles: ClinicUserRole[];
  clinic: Clinic | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  activateUser: (
    values: Record<string, unknown>,
    isLogin?: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  verifyMFA: (code: string) => Promise<void>;
  activatePin: (values: {
    pin: number;
    pin_confirmation: number;
  }) => Promise<void>;
  updateProfileRole: (role_id: number) => Promise<void>;
  resendMFACode: () => Promise<void>;
  mfaRequired: boolean;
  switchRole: (role: UserRole) => Promise<void>;
  updateUser: (params: Partial<User>) => Promise<void>;
  isLoading: boolean;
  pinActivated: boolean;
  requiredDocuments: RequiredDocument[];
  setRequiredDocuments: (documents: RequiredDocument[]) => void;
  register: (values: Record<string, unknown>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [pinActivated, setPinActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<ClinicUserRole[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useState<
    RequiredDocument[]
  >([]);
  const router = useRouter();

  useEffect(() => {
    // Check for existing session/token
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await apiClient.getCurrentUser();
          const {
            role,
            user,
            clinic,
            required_documents,
            pin_activated,
            user_roles,
          } = response;
          setUser(user);
          setRole(role);
          setClinic(clinic);
          setUserRoles(user_roles);
          setRequiredDocuments(required_documents);
          setPinActivated(pin_activated);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setLoading(false);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Add event listener for auth updates
  useEffect(() => {
    const handleAuthUpdate = (
      event: CustomEvent<{
        user: User;
        role: UserRole;
        clinic: Clinic;
        required_documents: RequiredDocument[];
      }>
    ) => {
      const { user, role, clinic, required_documents } = event.detail;
      setUser(user);
      setRole(role);
      setClinic(clinic);
      setRequiredDocuments(required_documents);
    };

    window.addEventListener('auth:update', handleAuthUpdate as EventListener);
    return () => {
      window.removeEventListener(
        'auth:update',
        handleAuthUpdate as EventListener
      );
    };
  }, []);

  const updateProfileRole = async (role_id: number) => {
    try {
      const response = await apiClient.updateProfileRole(role_id);
      setRole(response.role);
      window.location.reload();
    } catch (error) {
      console.error('Failed to update profile role:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Always require MFA after password check
      const authResponse = await apiClient.login({
        email,
        password,
      });
      setMfaRequired(authResponse.mfaRequired || true);
      setMfaToken(authResponse.token || null);
      router.push('/auth/mfa/confirm');
      return;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const register = async (values: Record<string, unknown>) => {
    setIsLoading(true);
    try {
      const authResponse = await apiClient.register(values);
      setMfaRequired(authResponse.mfaRequired || true);
      setMfaToken(authResponse.token || null);
      router.push('/auth/mfa/confirm');
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const activateUser = async (
    values: Record<string, unknown>,
    isLogin?: boolean
  ) => {
    setIsLoading(true);
    try {
      const response = await (isLogin
        ? apiClient.activateUser(values)
        : apiClient.activateClinicInvitation(values));

      setMfaRequired(response.mfaRequired || true);
      setMfaToken(response.token || null);
      router.push('/auth/mfa/confirm');
      return;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const verifyMFA = async (code: string) => {
    setIsLoading(true);
    try {
      if (!mfaToken) throw new Error('Missing MFA token');
      const response = await apiClient.verifyMFA(mfaToken, code);
      // Type guard for response
      if (!response || typeof response.token !== 'string' || !response.user) {
        throw new Error('Invalid MFA response');
      }
      localStorage.setItem('auth_token', response.token);
      setUser(response.user);
      setRole(response?.role || null);
      setUserRoles(response?.user_roles || []);
      setClinic(response?.clinic || null);
      setPinActivated(response?.pin_activated || false);
      setRequiredDocuments(response?.required_documents || []);
      setMfaRequired(false);
      setMfaToken(null);
    } catch (error) {
      console.error('MFA verification failed:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const activatePin = async (values: {
    pin: number;
    pin_confirmation: number;
  }) => {
    try {
      await apiClient.activatePin(values);
      setPinActivated(true);
    } catch (error) {
      console.error('Failed to activate PIN:', error);
      throw error;
    } finally {
    }
  };

  const resendMFACode = async () => {
    setIsLoading(true);
    try {
      if (!mfaToken) throw new Error('Missing MFA token');
      await apiClient.resendMFACode(mfaToken);
    } catch (error) {
      console.error('Failed to resend MFA code:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };
  const updateUser = async (params: Partial<User>) => {
    setUser((user) => {
      if (!user) return null;
      return { ...user, ...params };
    });
  };

  const switchRole = async (role: UserRole) => {
    setIsLoading(true);
    try {
      const response = await apiClient.switchRole(role);
      const { user, token } = response;
      if (token) {
        localStorage.setItem('auth_token', token);
      }
      setUser(user || null);
    } catch (error) {
      console.error('Failed to switch role:', error);
      throw error;
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('auth_token');
      setUser(null);
      setMfaRequired(false);
      router.push('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    role,
    userRoles,
    updateProfileRole,
    isSuperAdmin: role === UserRole.ADMIN && !clinic,
    clinic,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    activateUser,
    verifyMFA,
    resendMFACode,
    mfaRequired,
    switchRole,
    isLoading,
    requiredDocuments,
    setRequiredDocuments,
    register,
    pinActivated,
    activatePin,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
